import type { JSONSchema7, JSONSchema7Definition } from "json-schema"
import type { TYPE_KEYWORDS } from "./model"
import { JSON_STRING_FORMATS, JsonFieldType, NULL } from "./model"

/**
 * Describes the JsonField shape for the code editor's autocomplete and hover tooltips only. It
 * carries no constraints: `validateJsonField` decides validity, so a stale entry here costs a
 * suggestion, not a wrong verdict.
 */

const DESCRIPTION: JSONSchema7 = {
  type: "string",
  description: "Optional description of the field",
}

/** Matches `"string"` as well as its nullable form `["string", "null"]`, in either order. */
const isType = (type: JsonFieldType): JSONSchema7 => ({
  anyOf: [{ const: type }, { type: "array", contains: { const: type } }],
})

/**
 * The `properties` record must name exactly the keywords `TYPE_KEYWORDS` allows for this type,
 * so the docs here cannot drift from what `validateJsonField` accepts.
 */
const whenType = <T extends JsonFieldType>(
  type: T,
  properties: Record<(typeof TYPE_KEYWORDS)[T][number], JSONSchema7Definition>,
): JSONSchema7 => ({
  if: { properties: { type: isType(type) }, required: ["type"] },
  then: { properties: { ...properties, description: DESCRIPTION } },
})

const ENUM_DESCRIPTION = "Allowed values for the field"

export const jsonFieldMetaSchema: JSONSchema7 = {
  $schema: "http://json-schema.org/draft-07/schema#",
  $ref: "#/definitions/JsonField",
  definitions: {
    JsonField: {
      title: "JSON Field Schema",
      description:
        "Schema for a typed JSON field. The 'type' property determines which additional properties are available.",
      type: "object",
      required: ["type"],
      allOf: [
        {
          properties: {
            type: {
              description: `The field type. Nullable form: [T, "${NULL}"] or ["${NULL}", T].`,
              anyOf: [
                { type: "string", enum: [...Object.values(JsonFieldType)] },
                // items lets completion suggest the members of a nullable [T, "null"]
                // array; validity of the pairing is still enforced by validateJsonField.
                {
                  type: "array",
                  items: {
                    type: "string",
                    enum: [...Object.values(JsonFieldType), NULL],
                  },
                },
              ],
            },
            description: DESCRIPTION,
          },
        },
        whenType(JsonFieldType.string, {
          minLength: { type: "integer", description: "Minimum string length" },
          maxLength: { type: "integer", description: "Maximum string length" },
          format: {
            type: "string",
            enum: [...JSON_STRING_FORMATS],
            description: "Semantic format hint for the string value",
          },
          enum: {
            type: "array",
            items: { type: "string" },
            description: ENUM_DESCRIPTION,
          },
        }),
        whenType(JsonFieldType.number, {
          minimum: { type: "number", description: "Minimum value" },
          maximum: { type: "number", description: "Maximum value" },
          enum: {
            type: "array",
            items: { type: "number" },
            description: ENUM_DESCRIPTION,
          },
        }),
        whenType(JsonFieldType.integer, {
          minimum: { type: "number", description: "Minimum value" },
          maximum: { type: "number", description: "Maximum value" },
          enum: {
            type: "array",
            items: { type: "integer" },
            description: ENUM_DESCRIPTION,
          },
        }),
        whenType(JsonFieldType.boolean, {}),
        whenType(JsonFieldType.object, {
          properties: {
            type: "object",
            description: "Object properties mapping keys to JsonFields",
            additionalProperties: { $ref: "#/definitions/JsonField" },
          },
          required: {
            type: "array",
            items: { type: "string" },
            description: "Required property names",
          },
          additionalProperties: {
            const: false,
            description: "Must be false",
          },
        }),
        whenType(JsonFieldType.array, {
          items: {
            $ref: "#/definitions/JsonField",
            description: "Schema for array items",
          },
          minItems: { type: "integer", description: "Minimum array length" },
          maxItems: { type: "integer", description: "Maximum array length" },
        }),
      ],
    },
  },
}
